import { formatCurrency } from "@/utils/common";

/** Aggregated stats shown on the Family Hub dashboard. */
export interface FamilyDashboardStats {
  total_members: number;
  active_members: number;
  /** Sum of active family goal target amounts (API field: total_goals). */
  goals_target_total: number;
  /** Active (non-archived) goal count. */
  goals_count: number;
  total_assets: number;
  total_liabilities: number;
  net_worth: number;
}

export function formatMemberCount(count: number): string {
  const safe = Number.isFinite(count) ? Math.max(0, count) : 0;
  return `${safe} ${safe === 1 ? "member" : "members"}`;
}

/**
 * Prefer explicit net_worth when it is a real (non-zero) figure.
 * When net_worth is missing/NaN, or is 0 while total_assets > 0 (legacy RPC
 * often returns net_worth: 0 alongside populated assets), derive from assets − liabilities.
 */
export function resolveFamilyNetWorth(
  stats: Pick<FamilyDashboardStats, "net_worth" | "total_assets" | "total_liabilities"> | null
): number {
  if (!stats) return 0;
  const assets = Number.isFinite(stats.total_assets) ? stats.total_assets : 0;
  const liabilities = Number.isFinite(stats.total_liabilities) ? stats.total_liabilities : 0;
  const derived = assets - liabilities;
  const explicit =
    typeof stats.net_worth === "number" && Number.isFinite(stats.net_worth)
      ? stats.net_worth
      : null;

  if (explicit !== null && !(explicit === 0 && assets > 0)) {
    return explicit;
  }
  return derived;
}

/**
 * When dashboard RPC still reports ₹0 (legacy `public.assets` only) but Asset Map /
 * specialized holdings have a positive total, prefer the holdings total for display.
 */
export function mergeFamilyNetWorthWithHoldings(
  stats: FamilyDashboardStats | null,
  holdingsTotal: number
): FamilyDashboardStats | null {
  if (!stats) return null;
  const safeHoldings =
    typeof holdingsTotal === "number" && Number.isFinite(holdingsTotal) && holdingsTotal > 0
      ? holdingsTotal
      : 0;
  if (safeHoldings <= 0) return stats;

  const resolved = resolveFamilyNetWorth(stats);
  if (resolved !== 0) return stats;

  const liabilities = Number.isFinite(stats.total_liabilities) ? stats.total_liabilities : 0;
  return {
    ...stats,
    total_assets: Math.max(stats.total_assets ?? 0, safeHoldings),
    net_worth: safeHoldings - liabilities,
  };
}

export function formatFamilyNetWorth(
  stats: Pick<FamilyDashboardStats, "net_worth" | "total_assets" | "total_liabilities"> | null
): string {
  return formatCurrency(resolveFamilyNetWorth(stats));
}

/**
 * Format Family Net Worth Trend.
 */
export function formatFamilyNetWorthTrend(snapshots: { net_worth: number }[]): string {
  if (!snapshots.length) return "No change this month";
  if (snapshots.length === 1) return "No change this month";

  const first = snapshots[0]?.net_worth ?? 0;
  const last = snapshots[snapshots.length - 1]?.net_worth ?? 0;
  const delta = last - first;

  if (delta === 0) return "No change this month";
  const sign = delta > 0 ? "+" : "";
  return `${sign}${formatCurrency(delta)} this month`;
}
